// src/components/BCP/BCPFormContainer.tsx - Main BCP form container component
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyzeBCP, getBCPSuggestions, getBCPMitigationSuggestions, getContextSpecificSuggestions } from '../../services/api';

// Import types
import { AdditionalLocation, Step } from '../../types/bcp';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';

// Import custom hooks
import { useBCPFormState } from '../../hooks/useBCPFormState';
import { useBCPAISuggestions } from '../../hooks/useBCPAISuggestions';
import { useBCPModals } from '../../hooks/useBCPModals';
import { useBCPSavingStrategy } from '../../hooks/useBCPSavingStrategy';

// Import validation utilities
import {
  validateForm,
  canProceedToStep,
  getStepValidationErrors,
  createFieldChangeHandler,
  createFieldBlurHandler,
  shouldShowError,
  getFieldError
} from '../../utils/bcpValidation';

// Import components
import BusinessInfoStep from './BusinessInfoStep';
import RiskAssessmentStep from './RiskAssessmentStep';
import BusinessImpactStep from './BusinessImpactStep';
import OperationsStep from './OperationsStep';
import MitigationsStep from './MitigationsStep';
import EmployeeManagement from './EmployeeManagement';
import ServiceProviderManagement from './ServiceProviderManagement';
import ReviewStep from './ReviewStep';
import ProgressBar from './ProgressBar';
import NavigationButtons from './NavigationButtons';
import AIAnalysisPanel from './AIAnalysisPanel';
import ChapterChecklist from './ChapterChecklist';
import BCPModals from './BCPModals';
import BCPAssessment from './BCPAssessment';
import BCPLimitsModal from './BCPLimitsModal';
import { SaveStatusIndicator, SectionSaveButton } from './SaveComponents';

// Import hooks
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useBCPLimits } from '../../hooks/useBCPLimits';

const BCPFormContainer: React.FC = () => {
  const navigate = useNavigate();
  const { canAccessFeature } = useSubscriptionContext();

  // User preferences and assessment
  const { preferences, isFirstBCP, submitAssessment, loading: preferencesLoading } = useUserPreferences();
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [manualAssessmentTriggered, setManualAssessmentTriggered] = useState(false);

  // BCP limits
  const {
    limits,
    loading: limitsLoading,
    canCreateBCP
  } = useBCPLimits();


  // Form state management
  const formState = useBCPFormState();
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    touched,
    setTouched,
    bcpId,
    setBcpId,
    isEditing,
    isLoading,
    submitting,
    setSubmitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    setSubmitError,
    bcpGenerated,
    setBcpGenerated,
    setGeneratedBCPId,
    risks,
    setRisks,
    analysis,
    setAnalysis,
    suggestions,
    setSuggestions,
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    suggestionsApplied,
    setSuggestionsApplied,
    originalBCPData,
    employees,
    setEmployees,
    loadBCPData
  } = formState;


  // Modal state management
  const modalState = useBCPModals();
  const {
    showAnalysisModal,
    setShowAnalysisModal,
    showPreviewModal,
    setShowPreviewModal,
    previewBCPId,
    previewBCPName,
    showUpgradeModal,
    setShowUpgradeModal,
    showGenerationModal,
    setShowGenerationModal,
    generationStep: modalGenerationStep,
    setGenerationStep: setModalGenerationStep,
    generatedBCPId: modalGeneratedBCPId,
    setGeneratedBCPId: setModalGeneratedBCPId,
    handlePreviewBCP,
    handleShowUpgrade,
    handleShowGeneration
  } = modalState;

  // AI suggestions
  const aiSuggestions = useBCPAISuggestions(
    bcpId,
    formData,
    risks,
    setRisks,
    setFormData,
    setBcpId,
    suggestions,
    setSuggestions,
    isLoadingSuggestions,
    setIsLoadingSuggestions,
    suggestionsApplied,
    setSuggestionsApplied
  );

  // AI mitigations generation
  const [isGeneratingAIMitigations, setIsGeneratingAIMitigations] = useState(false);

  // Enhanced saving strategy
  const { autoSave, manualSave, criticalSave, savingStatus } = useBCPSavingStrategy(
    bcpId,
    isEditing,
    formData,
    risks,
    originalBCPData
  );

  // Refs for timeout cleanup
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Assessment completion handler
  const handleAssessmentComplete = useCallback(async (assessmentData: any) => {
    try {
      await submitAssessment(assessmentData);
      setAssessmentCompleted(true);
      setShowAssessment(false);
      setManualAssessmentTriggered(false);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      // Continue anyway
      setAssessmentCompleted(true);
      setShowAssessment(false);
      setManualAssessmentTriggered(false);
    }
  }, [submitAssessment]);

  const handleAssessmentSkip = useCallback(() => {
    setAssessmentCompleted(true);
    setShowAssessment(false);
    setManualAssessmentTriggered(false);
  }, []);

  // Manual assessment trigger handler
  const handleManualAssessmentTrigger = useCallback(() => {
    setManualAssessmentTriggered(true);
    setShowAssessment(true);
  }, []);

  const totalSteps = 6;
  const steps: Step[] = [
    { number: 1, title: 'Business Info', description: 'Company details & industry' },
    { number: 2, title: 'Risk Assessment', description: 'Identify potential threats' },
    { number: 3, title: 'Business Impact', description: 'Critical function analysis' },
    { number: 4, title: 'Operations', description: 'IT systems & suppliers' },
    { number: 5, title: 'Mitigations', description: 'Risk response strategies' },
    { number: 6, title: 'Review', description: 'Finalize & generate plan' }
  ];

  const [currentStep, setCurrentStep] = useState(1);

  // Load BCP data when component mounts
  useEffect(() => {
    loadBCPData();
  }, [loadBCPData]);

  // Show assessment for new BCP creation (first-time users) or when manually triggered
  useEffect(() => {
    // Don't show assessment until preferences are loaded
    if (preferencesLoading) return;

    console.log('🔍 [ASSESSMENT] Checking assessment logic:', {
      isEditing,
      isFirstBCP,
      assessmentCompleted,
      manualAssessmentTriggered,
      preferencesLoading,
      hasPreferences: preferences !== null
    });

    if ((!isEditing && isFirstBCP && !assessmentCompleted) || manualAssessmentTriggered) {
      console.log('🔍 [ASSESSMENT] Showing assessment');
      setShowAssessment(true);
    }
  }, [isEditing, isFirstBCP, assessmentCompleted, manualAssessmentTriggered, preferencesLoading, preferences]);

  // Monitor form data changes for debugging
  useEffect(() => {
    if (isEditing && formData.business_name) {
      console.log('🔍 [BCP FORM] Form data updated:', {
        business_type: formData.business_type,
        employee_count: formData.employee_count,
        annual_revenue: formData.annual_revenue
      });
    }
  }, [formData.business_type, formData.employee_count, formData.annual_revenue, isEditing, formData.business_name]);

  // Debug: Log when BusinessInfoStep should re-render
  useEffect(() => {
    console.log('🔍 [BCP FORM] BusinessInfoStep props:', {
      business_type: formData.business_type,
      employee_count: formData.employee_count,
      annual_revenue: formData.annual_revenue,
      isEditing
    });
  }, [formData.business_type, formData.employee_count, formData.annual_revenue, isEditing]);

  // Auto-save trigger on form data changes
  useEffect(() => {
    if (isEditing && bcpId) {
      autoSave(formData);
    }
  }, [
    formData,
    autoSave,
    isEditing,
    bcpId
  ]);

  // Cleanup all timeouts on component unmount
  useEffect(() => {
    const autoSaveTimeout = autoSaveTimeoutRef.current;
    const statusTimeout = statusTimeoutRef.current;

    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      if (statusTimeout) {
        clearTimeout(statusTimeout);
      }
    };
  }, []);

  // Check if current step can proceed
  const canProceed = useMemo((): boolean => {
    return canProceedToStep(currentStep, formData, risks);
  }, [currentStep, formData, risks]);

  // Field change handler
  const handleFieldChange = useMemo(() =>
    createFieldChangeHandler(
      formData,
      setFormData,
      errors,
      setErrors,
      touched,
      setTouched,
      submitError,
      setSubmitError
    ), [formData, setFormData, errors, setErrors, touched, setTouched, submitError, setSubmitError]
  );

  // Field blur handler
  const handleFieldBlur = useMemo(() =>
    createFieldBlurHandler(formData, setErrors, touched, setTouched),
    [formData, setErrors, touched, setTouched]
  );

  // AI mitigations generation handler
  const handleGenerateAIMitigations = useCallback(async () => {
    if (!bcpId) {
      console.error('BCP ID is required for AI mitigations generation');
      return;
    }

    setIsGeneratingAIMitigations(true);
    try {
      console.log('🤖 [AI MITIGATIONS] Starting mitigation-focused AI generation...');

      // Prepare current form data for context
      const contextData = {
        business_name: formData.business_name,
        business_type: formData.business_type,
        industry: formData.industry,
        employee_count: formData.employee_count,
        annual_revenue: formData.annual_revenue,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        existing_critical_functions: formData.critical_functions,
        existing_it_systems: formData.it_systems,
        existing_key_suppliers: formData.key_suppliers,
        risk_impact_assessments: formData.risk_impact_assessments,
        existing_mitigations: formData.mitigations,
        identified_risks: risks.primary_concerns,
        location_risks: risks.location_risks
      };

      const response = await getBCPMitigationSuggestions(bcpId, contextData);
      const aiSuggestions = response.suggestions;

      if (aiSuggestions.suggested_mitigations && aiSuggestions.suggested_mitigations.length > 0) {
        // Convert AI suggestions to Mitigation objects with full context
        const aiMitigations = aiSuggestions.suggested_mitigations.map((mitigation: any) => ({
          id: Math.random().toString(36).substr(2, 9),
          risk_name: mitigation.risk_name || 'General Risk',
          mitigation_strategy: mitigation.mitigation_strategy || 'Unnamed Strategy',
          detailed_strategy: mitigation.detailed_strategy || '',
          incident_response_steps: mitigation.incident_response_steps || [],
          responsible_party: mitigation.responsible_party || 'To be assigned',
          timeline: mitigation.timeline || 'To be scheduled',
          effectiveness: mitigation.effectiveness || 'high',
          status: mitigation.status || 'planned' as const,
          specific_actions: mitigation.specific_actions || [],
          resources_required: mitigation.resources_required || '',
          implementation_priority: mitigation.implementation_priority || '',
          maintenance_requirements: mitigation.maintenance_requirements || ''
        }));

        // Remove duplicates by checking for similar mitigation strategies for the same risk
        const existingMitigations = formData.mitigations || [];
        const filteredAIMitigations = aiMitigations.filter((aiMitigation: any) => {
          // Check if a similar mitigation already exists
          const isDuplicate = existingMitigations.some((existing: any) => {
            // Normalize strings for comparison
            const normalizeString = (str: string) =>
              str.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();

            const sameRisk = normalizeString(existing.risk_name) === normalizeString(aiMitigation.risk_name);
            const sameStrategy = normalizeString(existing.mitigation_strategy) === normalizeString(aiMitigation.mitigation_strategy);

            return sameRisk && sameStrategy;
          });

          return !isDuplicate;
        });

        // Add only non-duplicate AI mitigations to existing mitigations
        const updatedMitigations = [...existingMitigations, ...filteredAIMitigations];
        handleFieldChange('mitigations', updatedMitigations);

        console.log(`✅ [AI MITIGATIONS] Generated ${filteredAIMitigations.length} new context-aware AI mitigations (${aiMitigations.length - filteredAIMitigations.length} duplicates skipped)`);
      } else {
        console.log('⚠️ [AI MITIGATIONS] No AI suggestions available');
      }
    } catch (error) {
      console.error('❌ [AI MITIGATIONS] AI generation failed:', error);
    } finally {
      setIsGeneratingAIMitigations(false);
    }
  }, [bcpId, formData, handleFieldChange, risks.primary_concerns, risks.location_risks]);

  // Section-specific save handlers
  const saveOperations = useCallback(async () => {
    await Promise.all([
      manualSave('critical_functions', formData.critical_functions),
      manualSave('key_suppliers', formData.key_suppliers),
      manualSave('it_systems', formData.it_systems)
    ]);
  }, [manualSave, formData.critical_functions, formData.key_suppliers, formData.it_systems]);

  const saveRiskAssessment = useCallback(async () => {
    console.log('🔍 [SAVE STRATEGY] Saving risk assessment:', {
      primary_concerns: risks.primary_concerns,
      location_risks: risks.location_risks
    });
    await manualSave('risk_factors', { selected: risks.primary_concerns });
    await manualSave('threat_probability', { selected: risks.location_risks });
  }, [manualSave, risks]);

  const saveMitigations = useCallback(async () => {
    console.log('🔍 [SAVE STRATEGY] Saving mitigations:', formData.mitigations);
    await manualSave('mitigations', formData.mitigations);
  }, [manualSave, formData.mitigations]);

  // BIA field save handler - used by BusinessImpactStep
  const onSaveBIAField = useCallback(async (field: string, value: any) => {
    await manualSave(field, value);

    // No need to reload entire form data - the save operation already updates the backend
    // and the form state is already updated via handleFieldChange
  }, [manualSave]);

  // Navigation handlers
  const nextStep = useCallback(async () => {
    if (!canProceed) {
      if (currentStep === 1) {
        // Show validation errors
        const stepErrors = getStepValidationErrors(currentStep, formData);
        setErrors(stepErrors);
        const touchedFields: { [key: string]: boolean } = {};
        Object.keys(stepErrors).forEach(field => {
          touchedFields[field] = true;
        });
        setTouched(touchedFields);
      } else if (currentStep === 2) {
        alert('Please select at least one risk factor before proceeding.');
      } else if (currentStep === 3) {
        alert('Please add at least one critical function before proceeding.');
      }
      return;
    }

    // For critical transitions, ensure data is saved
    if (currentStep === 1 || currentStep === 4) {
      try {
        await criticalSave(formData);
      } catch (error) {
        console.error('Failed to save before navigation:', error);
        // Still allow navigation but show warning
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  }, [canProceed, currentStep, totalSteps, criticalSave, formData, setErrors, setTouched]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const navigateToStep = useCallback((stepNumber: number) => {
    if (stepNumber >= 1 && stepNumber <= totalSteps) {
      setCurrentStep(stepNumber);
    }
  }, [totalSteps]);

  // Additional location handlers
  const addAdditionalLocation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      additional_locations: [
        ...prev.additional_locations,
        { name: '', contact: '', address: '', state: '', zip: '', phone: '' }
      ]
    }));
  }, [setFormData]);

  const updateAdditionalLocationField = useCallback((
    index: number,
    field: keyof AdditionalLocation,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      additional_locations: prev.additional_locations.map((loc, i) =>
        i === index ? { ...loc, [field]: value } : loc
      )
    }));
  }, [setFormData]);

  const removeAdditionalLocation = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      additional_locations: prev.additional_locations.filter((_, i) => i !== index)
    }));
  }, [setFormData]);

  // Submit handler with critical save
  const handleSubmit = useCallback(async () => {
    // Check subscription access before proceeding
    if (!canAccessFeature('bcp_generation')) {
      handleShowUpgrade();
      return;
    }


    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setSubmitError('Please fix validation errors before submitting.');
      setErrors(validationErrors);
      return;
    }

    // Show generation modal
    handleShowGeneration();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Step 1: Save BCP
      const result = await criticalSave(formData);

      // Set BCP ID and generate AI analysis for both new and existing BCPs
      if (result && typeof result === 'object' && result !== null && 'id' in result) {
        setGeneratedBCPId(result.id);
        setModalGeneratedBCPId(result.id);
        setModalGenerationStep('generating');

        // Generate AI analysis for both new and existing BCPs
        try {
          const analyzed = await analyzeBCP(result.id);
          setAnalysis(analyzed.analysis);
          setBcpGenerated(true);
          setModalGenerationStep('complete');
        } catch (analysisError) {
          console.error('AI analysis failed:', analysisError);
          setModalGenerationStep('complete');
        }
      } else {
        setModalGenerationStep('complete');
      }

    } catch (error: any) {
      console.error('Error saving BCP:', error);
      setSubmitError(error.message || 'Failed to save BCP');
      setShowGenerationModal(false);
    } finally {
      setSubmitting(false);
    }
  }, [canAccessFeature, formData, criticalSave, setSubmitting, setSubmitError, setSubmitSuccess, handleShowUpgrade, handleShowGeneration, setGeneratedBCPId, setModalGeneratedBCPId, setModalGenerationStep, setAnalysis, setBcpGenerated, setShowGenerationModal, setErrors]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BusinessInfoStep
            additionalLocations={formData.additional_locations}
            onAddAdditionalLocation={addAdditionalLocation}
            onUpdateAdditionalLocationField={updateAdditionalLocationField}
            onRemoveAdditionalLocation={removeAdditionalLocation}
            form={formData}
            setForm={setFormData}
            handleFieldChange={handleFieldChange}
            handleFieldBlur={handleFieldBlur}
            shouldShowError={(field) => shouldShowError(field, touched, errors)}
            getFieldError={(field) => getFieldError(field, errors)}
          />
        );
      case 2:
        return (
          <div className="space-y-6">
            <RiskAssessmentStep
              risks={risks}
              setRisks={setRisks}
              suggestions={suggestions}
              isLoadingSuggestions={isLoadingSuggestions}
              onGetSuggestions={aiSuggestions.getAISuggestions}
              businessProfile={formData}
              suggestionsApplied={suggestionsApplied}
              userPreferences={preferences}
            />

            {/* Section Save Button */}
            <div className="flex justify-end">
              <SectionSaveButton
                section="risk_assessment"
                onSave={saveRiskAssessment}
                savingStatus={savingStatus}
                disabled={risks.primary_concerns.length === 0}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <BusinessImpactStep
            formData={formData}
            setFormData={setFormData}
            handleFieldChange={handleFieldChange}
            handleFieldBlur={handleFieldBlur}
            shouldShowError={(field) => shouldShowError(field, touched, errors)}
            getFieldError={(field) => getFieldError(field, errors)}
            onSaveBIAField={onSaveBIAField}
            risks={risks}
            userPreferences={preferences}
          />
        );
      case 4:
        return (
          <div className="space-y-8">
            <OperationsStep
              formData={formData}
              setFormData={setFormData}
              handleFieldChange={handleFieldChange}
              handleFieldBlur={handleFieldBlur}
              shouldShowError={(field) => shouldShowError(field, touched, errors)}
              getFieldError={(field) => getFieldError(field, errors)}
              suggestions={suggestions}
              onApplySuggestions={aiSuggestions.applySuggestions}
              bcpId={bcpId ? parseInt(bcpId) : undefined}
            />

            {/* Team Members and Service Providers */}
            {bcpId && (
              <div className="space-y-8">
                <EmployeeManagement
                  bcpId={parseInt(bcpId)}
                  onEmployeesChange={setEmployees}
                />
                <ServiceProviderManagement
                  bcpId={parseInt(bcpId)}
                  onServiceProvidersChange={() => {}}
                />
              </div>
            )}
            {!bcpId && (
              <div className="py-8 text-center text-gray-500">
                <p>Please save your BCP first to manage team members and service providers.</p>
              </div>
            )}

            {/* Single Save Button for All Operations */}
            <div className="flex justify-end">
              <button
                onClick={() => saveOperations()}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
              >
                Save Operations
              </button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <MitigationsStep
              risks={risks}
              formData={formData}
              setFormData={setFormData}
              handleFieldChange={handleFieldChange}
              handleFieldBlur={handleFieldBlur}
              shouldShowError={(field) => shouldShowError(field, touched, errors)}
              getFieldError={(field) => getFieldError(field, errors)}
              onGenerateAIMitigations={handleGenerateAIMitigations}
              isLoadingAIMitigations={isGeneratingAIMitigations}
            />

            {/* Section Save Button */}
            <div className="flex justify-end">
              <SectionSaveButton
                section="mitigations"
                onSave={saveMitigations}
                savingStatus={savingStatus}
                disabled={!formData.mitigations || formData.mitigations.length === 0}
              />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <ReviewStep
              additionalLocations={formData.additional_locations}
              analysis={analysis}
              formData={formData}
            />

            {/* Subscription Notice */}
            {!canAccessFeature('bcp_generation') && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Subscription Required
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      An Essential subscription is required to generate your Business Continuity Plan.
                      Click "Generate BCP" below to upgrade and unlock this feature.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Show assessment for first-time users
  if (showAssessment) {
    return (
      <BCPAssessment
        onComplete={handleAssessmentComplete}
        onSkip={handleAssessmentSkip}
        isFirstBCP={isFirstBCP}
        existingPreferences={preferences}
      />
    );
  }

  // Show only modal if user can't create BCP and it's a new BCP (not editing)
  if (!isEditing && !limitsLoading && limits && !canCreateBCP) {
    return (
      <>
        <BCPLimitsModal
          isOpen={true}
          onClose={() => {
            // Navigate back to continuity plans when modal is closed
            navigate('/bcp');
          }}
          limits={limits}
          loading={limitsLoading}
          onUpgrade={() => {
            // Navigate to subscription management
            window.location.href = '/subscription';
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">

          <div className="flex justify-between items-center">
            <div>
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                {isEditing ? 'Edit Business Continuity Plan' : 'Create Business Continuity Plan'}
              </h1>
              <p className="text-gray-600">
                {isEditing ? 'Update your existing business continuity plan.' : 'Let our AI guide you through creating a comprehensive BCP for your business'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {isLoading && (
                <div className="flex items-center text-blue-600">
                  <div className="mr-2 w-6 h-6 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                  <span>Loading BCP data...</span>
                </div>
              )}
              {isEditing && !isFirstBCP && (
                <button
                  onClick={handleManualAssessmentTrigger}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  title="Update your preferences and get package recommendations"
                >
                  Update Preferences
                </button>
              )}
              <SaveStatusIndicator savingStatus={savingStatus} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {/* Progress Bar */}
            <ProgressBar steps={steps} currentStep={currentStep} totalSteps={totalSteps} onStepClick={navigateToStep} />

            {/* Form Content */}
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl">
              {/* Success/Error Messages */}
              {submitSuccess && !bcpGenerated && (
                <div className="p-4 bg-green-50 border-b border-green-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        {isEditing ? 'BCP updated successfully!' : 'BCP created successfully!'}
                      </p>
                      <p className="text-sm text-green-700">
                        {isEditing ? 'Your changes have been saved.' : 'Analysis in progress...'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="p-4 bg-red-50 border-b border-red-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Saving Strategy Error Messages */}
              {Object.entries(savingStatus.errors).map(([section, error]) =>
                error && (
                  <div key={section} className="p-4 bg-red-50 border-b border-red-200">
                    <div className="flex items-center">
                      <svg className="mr-3 w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )
              )}

              <div className="p-8">
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="text-center">
                      <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                      <p className="text-gray-600">Loading BCP data...</p>
                    </div>
                  </div>
                ) : (
                  renderStepContent()
                )}
              </div>

              {/* Navigation Buttons */}
              <NavigationButtons
                steps={steps}
                currentStep={currentStep}
                totalSteps={totalSteps}
                onPrevStep={prevStep}
                onNextStep={nextStep}
                onSubmit={handleSubmit}
                submitting={submitting || savingStatus.isCriticalSaving}
                canProceed={canProceed}
              />
            </div>
          </div>

          {/* Right Sidebar - Chapter Checklist on Review step (step 6), AI Analysis on other steps */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {currentStep === 6 ? (
                <ChapterChecklist
                  formData={formData}
                  currentStep={currentStep}
                  onNavigateToStep={navigateToStep}
                />
              ) : (
                <AIAnalysisPanel
                  formData={formData}
                  currentStep={currentStep}
                  isVisible={currentStep < 6}
                  risks={risks}
                  employees={employees}
                  bcpId={bcpId}
                  isEditing={isEditing}
                />
              )}
            </div>
          </div>
        </div>

        {/* AI Assistant Preview - Show before BCP generation */}
        {!bcpGenerated && (
          <div className="p-6 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
            <div className="flex items-start space-x-4">
              <div className="flex flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">AI-Powered BCP Generation</h3>
                <p className="text-sm text-gray-600">
                  Our AI analyzes your business information and generates a comprehensive Business Continuity Plan
                  aligned with ISO 22301:2019, tailored to your business needs and risk profile.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* All Modals */}
      <BCPModals
        showAnalysisModal={showAnalysisModal}
        setShowAnalysisModal={setShowAnalysisModal}
        analysis={analysis}
        bcpName={formData.business_name}
        showPreviewModal={showPreviewModal}
        setShowPreviewModal={setShowPreviewModal}
        previewBCPId={previewBCPId}
        previewBCPName={previewBCPName}
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        showGenerationModal={showGenerationModal}
        setShowGenerationModal={setShowGenerationModal}
        generationStep={modalGenerationStep}
        generatedBCPId={modalGeneratedBCPId}
        formData={formData}
        onPreviewBCP={handlePreviewBCP}
      />

    </>
  );
};

export default BCPFormContainer;
